import { toast } from 'react-toastify';
import AppLoading from '../components/AppLoading';
import PageEditorView from '../components/PageEditorView';
import React, { useState, useEffect, useRef } from 'react';
import { useDashboardViews, DashboardView, dashboardViewsAPIsService } from '../hooks/useDashboardViews';

interface DragItem {
  id: number;
  index: number;
}

export const isAdminDashboard = (dashboard: DashboardView) =>
  dashboard.order === -1;

const DashboardLayoutPage: React.FC = () => {
  const { dashboardViews, loading, error, refetch } = useDashboardViews(true, true); // Show all dashboards including disabled ones
  const [localDashboards, setLocalDashboards] = useState<DashboardView[]>([]);
  const [adminDashboards, setAdminDashboards] = useState<DashboardView[]>([]);
  const [draggedItem, setDraggedItem] = useState<DragItem | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDragLoading, setIsDragLoading] = useState(false);
  const [editingPageId, setEditingPageId] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);


  // Initialize local state when dashboardViews change
  useEffect(() => {
    if (dashboardViews.length > 0) {
      const admins = dashboardViews.filter(isAdminDashboard);
      const mains = dashboardViews.filter((dashboard) => !isAdminDashboard(dashboard));
      setLocalDashboards([...mains]);
      setAdminDashboards([...admins]);
    }
  }, [dashboardViews]);

  // Focus input when editing starts
  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingId]);

  const handleDragStart = (e: React.DragEvent, id: number, index: number) => {
    setDraggedItem({ id, index });
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setIsDragging(false);
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (!draggedItem || draggedItem.index === dropIndex) {
      setDraggedItem(null);
      setIsDragging(false);
      setDragOverIndex(null);
      return;
    }

    const newDashboards = [...localDashboards];

    // Swap the dragged item with the drop target
    [newDashboards[draggedItem.index], newDashboards[dropIndex]] = [
      newDashboards[dropIndex],
      newDashboards[draggedItem.index]
    ];

    setLocalDashboards(newDashboards);
    setDraggedItem(null);
    setIsDragging(false);
    setDragOverIndex(null);

    // Update order in database silently (no loader)
    await updateDashboardOrderSilent(newDashboards);
  };

  const updateDashboardOrder = async (dashboards: DashboardView[]) => {
    try {
      setIsUpdating(true);

      const updates = dashboards.map((dashboard, index) => ({
        id: dashboard.id,
        order: index + 1
      }));

      await dashboardViewsAPIsService.updateDashboardOrder(updates);

      // Refetch to ensure consistency
      await refetch();

      toast.success('Dashboard order updated successfully!');
    } catch (error) {
      console.error('Error updating dashboard order:', error);
      toast.error('Failed to update dashboard order');
      // Revert local changes
      setLocalDashboards([...dashboardViews]);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateDashboardOrderSilent = async (dashboards: DashboardView[]) => {
    try {
      setIsDragLoading(true);

      const updates = dashboards.map((dashboard, index) => ({
        id: dashboard.id,
        order: index + 1
      }));

      await dashboardViewsAPIsService.updateDashboardOrder(updates);

      // Refetch to ensure consistency without showing loader
      await refetch();
    } catch (error) {
      console.error('Error updating dashboard order:', error);
      toast.error('Failed to update dashboard order');
      // Revert local changes
      setLocalDashboards([...dashboardViews]);
    } finally {
      setIsDragLoading(false);
    }
  };

  const handleDoubleClick = (dashboard: DashboardView) => {
    setEditingId(dashboard.id);
    setEditingName(dashboard.name);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingName(e.target.value);
  };

  const handleNameSubmit = async () => {
    if (!editingId || !editingName.trim()) {
      setEditingId(null);
      setEditingName('');
      return;
    }

    try {
      setIsUpdating(true);

      await dashboardViewsAPIsService.updateDashboardName(editingId, editingName.trim());

      // Update local state
      setLocalDashboards(prev => 
        prev.map(dashboard => 
          dashboard.id === editingId 
            ? { ...dashboard, name: editingName.trim() }
            : dashboard
        )
      );

      setEditingId(null);
      setEditingName('');

      toast.success('Dashboard name updated successfully!');
    } catch (error) {
      console.error('Error updating dashboard name:', error);
      toast.error('Failed to update dashboard name');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSubmit();
    } else if (e.key === 'Escape') {
      setEditingId(null);
      setEditingName('');
    }
  };

  const handleRestoreDefault = async () => {
    try {
      setIsUpdating(true);

      await dashboardViewsAPIsService.restoreDefaultLayout();

      // Refetch data
      await refetch();

      setShowRestoreConfirm(false);
      toast.success('Dashboard layout restored to default successfully!');
    } catch (error) {
      console.error('Error restoring defaults:', error);
      toast.error('Failed to restore default layout');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditPage = (pageId: number) => {
    setEditingPageId(pageId);
  };

  const handleBackFromEditor = () => {
    setEditingPageId(null);
    // Refetch dashboard views to ensure we have the latest data
    refetch();
  };

  const handleToggleStatus = async (dashboard: DashboardView) => {
    const newStatus = dashboard.isActive === 1 ? 0 : 1;
    
    try {
      setIsUpdating(true);

      await dashboardViewsAPIsService.updateDashboardStatus(dashboard.id, newStatus);

      // Update local state
      const updateDashboard = (d: DashboardView) =>
        d.id === dashboard.id ? { ...d, isActive: newStatus } : d;

      if (isAdminDashboard(dashboard)) {
        setAdminDashboards(prev => prev.map(updateDashboard));
      } else {
        setLocalDashboards(prev => prev.map(updateDashboard));
      }

      toast.success(`Dashboard ${newStatus === 1 ? 'enabled' : 'disabled'} successfully!`);
      
      // Refetch to ensure consistency
      await refetch();
    } catch (error) {
      console.error('Error updating dashboard status:', error);
      toast.error('Failed to update dashboard status');
    } finally {
      setIsUpdating(false);
    }
  };

  // Show PageEditorView if editing a page
  if (editingPageId) {
    const editingDashboard = [...localDashboards, ...adminDashboards].find(d => d.id === editingPageId);
    if (editingDashboard) {
      return (
        <PageEditorView
          dashboard={editingDashboard}
          onBack={handleBackFromEditor}
          onSave={() => {
            toast.success('Page saved successfully!');
            handleBackFromEditor();
          }}
        />
      );
    }
  }

  if (loading && localDashboards.length === 0) {
    return (
      <div className="dashboard-layout-page">
        <div className="loading-container">
          <AppLoading />
          <p>Loading dashboard layout...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-layout-page">
        <div className="error-container">
          <h3>Error loading dashboard layout</h3>
          <p>{error}</p>
          <button onClick={refetch} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout-page">
      <div className="layout-header">
        <div className="header-content">
          <h2>Dashboard Layout</h2>
          <p>Drag and drop to reorder pages, double-click to rename</p>
        </div>
        <button
          className="btn btn-warning restore-btn"
          onClick={() => setShowRestoreConfirm(true)}
          disabled={isUpdating}
        >
          {isUpdating ? 'Updating...' : 'Restore to Default'}
        </button>
      </div>

      <div className="layout-content">
        <div className={`tabs-container ${isDragLoading ? 'loading-blur' : ''}`}>
          {localDashboards.map((dashboard, index) => (
            <div
              key={dashboard.id}
              draggable
              onDragEnd={handleDragEnd}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragStart={(e) => handleDragStart(e, dashboard.id, index)}
              className={`tab-item ${draggedItem?.id === dashboard.id ? 'dragging' : ''} ${dragOverIndex === index && draggedItem?.id !== dashboard.id ? 'drag-over' : ''}`}
            >
              {editingId === dashboard.id ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={editingName}
                  onChange={handleNameChange}
                  onBlur={handleNameSubmit}
                  onKeyDown={handleKeyPress}
                  className="tab-edit-input"
                />
              ) : (
                <div className="tab-content">
                  <div className="tab-header-row">
                    <div 
                      className={`tab-name editable-name ${dashboard.isActive === 0 ? 'disabled' : ''}`}
                      onClick={() => handleDoubleClick(dashboard)}
                      title="Edit name"
                    >
                      {dashboard.name}
                    </div>
                    <div className="tab-controls">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={dashboard.isActive === 1}
                          onChange={() => handleToggleStatus(dashboard)}
                          disabled={isUpdating}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  </div>
                  <div className="tab-description">{dashboard.description}</div>
                  <div className="tab-actions">
                    <button 
                      className="btn btn-primary edit-page-btn"
                      onClick={() => handleEditPage(dashboard.id)}
                      disabled={dashboard.isActive === 0}
                    >
                      Edit Graphs
                    </button>
                    <div className="tab-order">Order: {dashboard.order}</div>
                  </div>
                </div>
              )}
              <div className="tab-drag-handle">
                <div className="drag-icon">⋮⋮</div>
                <div className="drag-overlay">Drag to reorder</div>
              </div>
            </div>
          ))}
        </div>

        {localDashboards.length === 0 && (
          <div className="empty-state">
            <p>No dashboard pages found</p>
          </div>
        )}

        {adminDashboards.length > 0 && (
          <div className="admin-dashboards-container">
            <hr className="admin-dashboards-divider" />
            <div className="admin-dashboards-header">
              <h3>Admin Dashboards</h3>
              <p>These dashboards cannot be reordered. Use the toggle to enable or disable access.</p>
            </div>
            <div className="admin-dashboards-grid">
              {adminDashboards.map((dashboard) => (
                <div key={dashboard.id} className="tab-item admin-tab-item">
                  <div className="tab-content">
                    <div className="tab-header-row">
                      <div
                        className={`tab-name ${dashboard.isActive === 0 ? 'disabled' : ''}`}
                      >
                        {dashboard.name}
                      </div>
                      <div className="tab-controls">
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={dashboard.isActive === 1}
                            onChange={() => handleToggleStatus(dashboard)}
                            disabled={isUpdating}
                          />
                          <span className="slider"></span>
                        </label>
                      </div>
                    </div>
                    <div className="tab-description">{dashboard.description}</div>
                    <div className="tab-actions">
                      <button
                        className="btn btn-primary edit-page-btn"
                        onClick={() => handleEditPage(dashboard.id)}
                        disabled={dashboard.isActive === 0}
                      >
                        Edit Graphs
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Drag Loading Overlay */}
        {isDragLoading && (
          <div className="drag-loading-overlay">
              <AppLoading />
          </div>
        )}
      </div>

      {/* Restore Confirmation Modal */}
      {showRestoreConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Restore to Default Layout</h3>
            <p>Are you sure you want to restore the dashboard layout to its default configuration? This action cannot be undone.</p>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowRestoreConfirm(false)}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleRestoreDefault}
                disabled={isUpdating}
              >
                {isUpdating ? 'Restoring...' : 'Confirm Restore'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DashboardLayoutPage;
